import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { FormProvider, useForm } from 'react-hook-form'

import { IS_CONVERSATION_WITH_AI_ENABLED } from 'configs/toggle'
import ConversationPageApp from 'app/ConversationPage'
import ErrorBoundary from 'app/ConversationPage/components/ErrorBoundary'
import { ForbiddenError } from 'app/ConversationPage/utils/customError'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'
import { useConversationMessageStore } from 'stores/conversation'
import useExperiment from 'app/ConversationPage/hooks/useExperiment'
import { getDeviceId } from 'utils/deviceId'
import { AbortRequestService } from 'app/ConversationPage/utils/api'
import { useExperimentsContext } from 'utils/providers/useExperimentsContext'
import { IS_CONVERSATION_WITH_AI_NEW_ENTRY_POINT_ENABLED } from 'configs/toggle'

export async function getServerSideProps(ctx) {
  return {
    props: {
      appDeviceID: getDeviceId(ctx),
    },
  }
}

export const ConversationPage = ({ appDeviceID = '' }) => {
  const [isGuruAIExperimentVariantEnabled, setGuruAIExperimentVariantEnabled] =
    React.useState<boolean | undefined>(undefined)
  const router = useRouter()
  const { setLastError, setErrorBoundary } = useConversationMessageStore()
  const { SETrackConversationPage } = useSETrackEvent()
  const { getExperimentStatus: getGuruAIExperimentStatus } =
    useExperiment(appDeviceID)
  const abortController = AbortRequestService.getInstance()

  const { getExperimentByName, isFetchExperimentsSuccess } =
    useExperimentsContext()
  const experimentGuruAIRollout = getExperimentByName('guru_ai_rollout')

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      userMessage: '',
    },
  })

  const resetErrors = React.useCallback(() => {
    setErrorBoundary(undefined)
    setLastError(undefined)
  }, [setErrorBoundary, setLastError])

  const isGuruAIEligible = React.useMemo(
    () =>
      (IS_CONVERSATION_WITH_AI_ENABLED &&
        isGuruAIExperimentVariantEnabled) as boolean,
    [isGuruAIExperimentVariantEnabled]
  )

  const errorHandler = (error) => {
    if (error instanceof ForbiddenError) {
      SETrackConversationPage('GURU_AI_NO_ACCESS', {
        event_trigger: 'HOMEPAGE',
        screen_name: 'HOMEPAGE',
      })
    }
  }

  const fetchExperiment = React.useCallback(async () => {
    if (IS_CONVERSATION_WITH_AI_NEW_ENTRY_POINT_ENABLED) {
      if (isFetchExperimentsSuccess) {
        setGuruAIExperimentVariantEnabled(
          experimentGuruAIRollout?.treatment === 'variant'
        )
      }
    } else {
      try {
        const isVariantEnabled = await getGuruAIExperimentStatus()
        setGuruAIExperimentVariantEnabled(isVariantEnabled)
      } catch (e) {
        setGuruAIExperimentVariantEnabled(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experimentGuruAIRollout?.treatment, isFetchExperimentsSuccess])

  React.useEffect(() => {
    fetchExperiment()
  }, [fetchExperiment])

  React.useEffect(() => {
    window.onbeforeunload = () => resetErrors()

    window.addEventListener('beforeunload', () => {
      abortController.abortRequest()
      resetErrors()
    })

    return () => {
      abortController.abortRequest()
      resetErrors()
      document.removeEventListener('beforeunload', resetErrors)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // check if the experiment variant is not yet fetched
  if (isGuruAIExperimentVariantEnabled === undefined) {
    return null
  }

  return (
    <>
      <Head>
        <title>Asisten Guru - Merdeka Mengajar</title>
      </Head>
      <FormProvider {...methods}>
        <ErrorBoundary resetKeys={[router.asPath]} onError={errorHandler}>
          <ConversationPageApp isGuruAIEligible={isGuruAIEligible} />
        </ErrorBoundary>
      </FormProvider>
    </>
  )
}

export default ConversationPage
